import React, { useState, useEffect } from 'react';
import { X, Layers, ChevronDown, ChevronUp, Globe, Terminal, FileCode, User, Layout } from 'lucide-react';

const API_BASE = '/be/api/v1';

/**
 * EnvPreviewModal - Shows the compiled runtime environment for a profile
 * 
 * Displays:
 * - Layer breakdown (Global → Space → Launcher → Profile → User)
 * - Final merged environment after interpolation
 */
const EnvPreviewModal = ({ isOpen, onClose, profileId, profileName, spaceId = null, globalEnvId = null }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expandedLayers, setExpandedLayers] = useState({});

  useEffect(() => {
    if (isOpen && profileId) {
      fetchPreview();
    }
  }, [isOpen, profileId]);

  const fetchPreview = async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${API_BASE}/environments/preview/${profileId}`;
      const params = new URLSearchParams();
      if (spaceId) params.append('space_id', spaceId);
      if (globalEnvId) params.append('global_env_id', globalEnvId);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await fetch(url);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to load preview');
      }
      const result = await res.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const toggleLayer = (index) => {
    setExpandedLayers(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case 'global': return <Globe size={14} className="text-yellow-400" />;  // Studio-wide branding
      case 'space': return <Layout size={14} className="text-blue-400" />;     // Space-specific config
      case 'launcher': return <Terminal size={14} className="text-orange-400" />;
      case 'profile': return <FileCode size={14} className="text-purple-400" />;
      case 'profile_inline': return <FileCode size={14} className="text-purple-300" />;
      case 'user': return <User size={14} className="text-green-400" />;
      default: return <Layers size={14} className="text-gray-400" />;
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'global': return 'border-yellow-500/30 bg-yellow-500/5';  // Studio-wide branding
      case 'space': return 'border-blue-500/30 bg-blue-500/5';       // Space-specific config
      case 'launcher': return 'border-orange-500/30 bg-orange-500/5';
      case 'profile': return 'border-purple-500/30 bg-purple-500/5';
      case 'profile_inline': return 'border-purple-400/30 bg-purple-400/5';
      case 'user': return 'border-green-500/30 bg-green-500/5';
      default: return 'border-gray-700 bg-gray-800/50';
    }
  };

  const getLevelLabel = (level) => {
    switch (level) {
      case 'global': return 'Global (Studio)';
      case 'space': return 'Space';
      case 'launcher': return 'Launcher';
      case 'profile': return 'Profile Set';
      case 'profile_inline': return 'Profile Inline';
      case 'user': return 'User Override';
      default: return level;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60]">
      <div className="bg-gray-900 rounded-xl border border-gray-700 w-full max-w-3xl mx-4 shadow-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Layers className="text-green-400" size={20} />
            <div>
              <h2 className="text-lg font-semibold text-white">Environment Preview</h2>
              <p className="text-sm text-gray-400">
                {profileName || 'Profile'} - Runtime Environment
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-white rounded"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-400" />
            </div>
          ) : error ? (
            <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300">
              {error}
            </div>
          ) : data ? (
            <div className="space-y-6">
              {/* Info Banner */}
              <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700 text-sm text-gray-400">
                <p>
                  Environment compiled from <strong className="text-white">{data.layers?.length || 0}</strong> layers.
                  Variables with <code className="px-1 py-0.5 bg-gray-900 rounded text-green-400">%VAR_NAME%</code> are 
                  interpolated with values from parent layers.
                </p>
              </div>

              {/* Layers Breakdown */}
              <div>
                <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                  <Layers size={14} />
                  Layer Breakdown
                  <span className="text-xs text-gray-500 font-normal">(in order of application)</span>
                </h3>
                
                {data.layers?.length === 0 ? (
                  <p className="text-gray-500 text-sm italic">No environment layers configured.</p>
                ) : (
                  <div className="space-y-2">
                    {data.layers?.map((layer, index) => (
                      <div 
                        key={index}
                        className={`border rounded-lg overflow-hidden ${getLevelColor(layer.level)}`}
                      >
                        <div 
                          className="flex items-center gap-3 p-3 cursor-pointer hover:bg-white/5"
                          onClick={() => toggleLayer(index)}
                        >
                          <div className="flex items-center gap-2 flex-1">
                            <span className="w-6 h-6 flex items-center justify-center bg-gray-800 rounded text-xs text-gray-400">
                              {index + 1}
                            </span>
                            {getLevelIcon(layer.level)}
                            <span className="text-xs text-gray-500">{getLevelLabel(layer.level)}</span>
                            <span className="text-sm text-white font-medium">{layer.name}</span>
                            <span className="text-xs text-gray-500">
                              ({Object.keys(layer.variables || {}).length} vars)
                            </span>
                          </div>
                          {expandedLayers[index] ? 
                            <ChevronUp size={14} className="text-gray-400" /> : 
                            <ChevronDown size={14} className="text-gray-400" />
                          }
                        </div>
                        
                        {expandedLayers[index] && (
                          <div className="border-t border-gray-700/50 p-3 bg-gray-900/50">
                            {Object.keys(layer.variables || {}).length === 0 ? (
                              <p className="text-gray-500 text-xs italic">No variables in this layer</p>
                            ) : (
                              <div className="font-mono text-xs space-y-1">
                                {Object.entries(layer.variables).map(([key, value]) => (
                                  <div key={key} className="flex">
                                    <span className="text-blue-400 min-w-0 break-all">{key}</span>
                                    <span className="text-gray-500 mx-1">=</span>
                                    <span className="text-gray-300 min-w-0 break-all">{value}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Final Merged Environment */}
              <div>
                <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                  <Terminal size={14} className="text-green-400" />
                  Final Environment
                  <span className="text-xs text-gray-500 font-normal">
                    ({data.variable_count} variables, after interpolation)
                  </span>
                </h3>
                
                <div className="bg-gray-950 rounded-lg border border-green-500/30 p-4 max-h-64 overflow-auto">
                  {Object.keys(data.merged_environment || {}).length === 0 ? (
                    <p className="text-gray-500 text-sm italic">No environment variables configured.</p>
                  ) : (
                    <div className="font-mono text-xs space-y-1">
                      {Object.entries(data.merged_environment)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([key, value]) => (
                          <div key={key} className="flex">
                            <span className="text-green-400 min-w-0 break-all">{key}</span>
                            <span className="text-gray-500 mx-1">=</span>
                            <span className="text-gray-300 min-w-0 break-all">{value}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Copy to Clipboard */}
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    const envText = Object.entries(data.merged_environment || {})
                      .map(([k, v]) => `${k}=${v}`)
                      .join('\n');
                    navigator.clipboard.writeText(envText);
                  }}
                  className="px-3 py-1.5 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded transition-colors"
                >
                  Copy to Clipboard
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default EnvPreviewModal;

